import React, { useMemo } from 'react';
import { Calendar, Phone, Crown, MapPin, Sun, Moon, Lock } from 'lucide-react';
import { parseLocalDate, formatYMD } from '../utils/dateHelpers';

/**
 * AvailabilityCalendar — employer-facing availability view for a tradesperson.
 *
 * Privacy: the calendar shows ONLY high-level day buckets
 *   (available / evening_only / weekend_only / booked / unavailable / unknown).
 * It NEVER reveals personal schedule (e.g. day-job hours).
 *
 * Props:
 *   availability: payload returned from /api/tradespeople/profile/{user_id} -> availability
 *   isUnlocked: boolean — controls whether richer labels (next available dates list,
 *               areas-willing-to-work) are shown.
 */

const STATUS_STYLES = {
  available:    { bg: 'bg-emerald-500',  ring: 'ring-emerald-600',  label: 'Available' },
  evening_only: { bg: 'bg-indigo-400',   ring: 'ring-indigo-500',   label: 'Evenings' },
  weekend_only: { bg: 'bg-amber-400',    ring: 'ring-amber-500',    label: 'Weekends' },
  booked:       { bg: 'bg-rose-500',     ring: 'ring-rose-600',     label: 'Booked' },
  unavailable:  { bg: 'bg-slate-300',    ring: 'ring-slate-400',    label: 'Unavailable' },
  unknown:      { bg: 'bg-slate-100',    ring: 'ring-slate-200',    label: 'Not set' },
};

function formatNiceDate(iso) {
  return formatYMD(iso, { weekday: 'short', day: 'numeric', month: 'short' });
}

export default function AvailabilityCalendar({ availability, isUnlocked = false }) {
  const days = availability?.calendar || [];

  // Group into weeks for grid display
  const weeks = useMemo(() => {
    if (!days.length) return [];
    const result = [];
    // Pad start so first row aligns to Monday
    const firstDate = parseLocalDate(days[0].date) || new Date();
    // JS getDay: 0=Sun..6=Sat. We want Mon-first, so leading = (getDay+6)%7
    const leading = (firstDate.getDay() + 6) % 7;
    const padded = [
      ...Array(leading).fill(null),
      ...days,
    ];
    while (padded.length % 7 !== 0) padded.push(null);
    for (let i = 0; i < padded.length; i += 7) result.push(padded.slice(i, i + 7));
    return result;
  }, [days]);

  if (!availability) {
    return (
      <div className="card mb-6" data-testid="availability-section-empty">
        <p className="text-slate-500 text-sm">Availability not provided.</p>
      </div>
    );
  }

  const weekDayLabels = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

  return (
    <div className="card mb-6" data-testid="availability-section">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-bold text-slate-900 flex items-center gap-2">
          <Calendar className="h-5 w-5 text-orange-500" />
          Availability
        </h3>
        <div className="flex flex-wrap items-center gap-2">
          {availability.is_on_call && (
            <span
              className="inline-flex items-center gap-1 bg-emerald-100 border border-emerald-300 text-emerald-800 px-2.5 py-1 rounded-full text-xs font-bold"
              data-testid="availability-on-call-badge"
            >
              <Phone className="h-3 w-3" />
              On Call
            </span>
          )}
          {availability.is_priority_boosted && (
            <span
              className="inline-flex items-center gap-1 bg-orange-100 border border-orange-300 text-orange-800 px-2.5 py-1 rounded-full text-xs font-bold"
              data-testid="availability-priority-boost-badge"
            >
              <Crown className="h-3 w-3" />
              Priority
            </span>
          )}
        </div>
      </div>

      {/* Summary line */}
      <p className="text-sm text-slate-700 mb-4" data-testid="availability-summary">
        <span className="font-medium">{availability.summary || 'Has availability'}</span>
        {!isUnlocked && (
          <span className="ml-2 inline-flex items-center gap-1 text-xs text-slate-400">
            <Lock className="h-3 w-3" />
            Unlock for full schedule
          </span>
        )}
      </p>

      {/* Next-available dates (full only) */}
      {isUnlocked && availability.next_available_dates?.length > 0 && (
        <div className="mb-4 text-sm text-slate-700" data-testid="next-available-dates">
          <span className="font-medium text-slate-900">Next available:</span>{' '}
          {availability.next_available_dates.map((d, i) => (
            <span key={d} className="inline-block">
              {i > 0 && <span className="text-slate-400">,&nbsp;</span>}
              {formatNiceDate(d)}
            </span>
          ))}
        </div>
      )}

      {/* Calendar grid */}
      <div className="bg-slate-50 rounded-lg p-3 mb-4" data-testid="availability-calendar-grid">
        <div className="grid grid-cols-7 gap-1.5 mb-1">
          {weekDayLabels.map((d, i) => (
            <div key={`hdr-${i}`} className="text-center text-[10px] font-semibold text-slate-500 uppercase tracking-wide">
              {d}
            </div>
          ))}
        </div>
        <div className="space-y-1.5">
          {weeks.map((week, wi) => (
            <div key={`w-${wi}`} className="grid grid-cols-7 gap-1.5">
              {week.map((cell, ci) => {
                if (!cell) {
                  return <div key={`c-${wi}-${ci}`} className="aspect-square" />;
                }
                const style = STATUS_STYLES[cell.status] || STATUS_STYLES.unknown;
                const dateNum = (parseLocalDate(cell.date) || new Date()).getDate();
                return (
                  <div
                    key={cell.date}
                    title={`${formatNiceDate(cell.date)} – ${style.label}`}
                    data-testid={`calendar-day-${cell.date}`}
                    data-status={cell.status}
                    className={`aspect-square rounded-md ${style.bg} flex items-center justify-center text-[10px] font-bold text-white/95 ring-1 ${style.ring}`}
                  >
                    {dateNum}
                  </div>
                );
              })}
            </div>
          ))}
        </div>
      </div>

      {/* Legend */}
      <div className="flex flex-wrap gap-x-4 gap-y-2 text-xs text-slate-600 mb-4" data-testid="availability-legend">
        {Object.entries(STATUS_STYLES).map(([k, s]) => (
          <span key={k} className="flex items-center gap-1.5">
            <span className={`inline-block w-3 h-3 rounded ${s.bg}`} />
            {s.label}
          </span>
        ))}
      </div>

      {/* Evenings / weekends / areas */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-sm">
        {(availability.evenings_available || availability.weekends_available) && (
          <div className="flex items-center gap-2 text-slate-700" data-testid="availability-extras">
            {availability.evenings_available && (
              <span className="inline-flex items-center gap-1 bg-indigo-50 border border-indigo-200 text-indigo-800 px-2 py-0.5 rounded-full text-xs font-medium">
                <Moon className="h-3 w-3" />
                Evenings
              </span>
            )}
            {availability.weekends_available && (
              <span className="inline-flex items-center gap-1 bg-amber-50 border border-amber-200 text-amber-800 px-2 py-0.5 rounded-full text-xs font-medium">
                <Sun className="h-3 w-3" />
                Weekends
              </span>
            )}
          </div>
        )}
        {(availability.outward_postcode || (availability.work_areas?.length ?? 0) > 0) && (
          <div className="flex items-start gap-2 text-slate-700" data-testid="availability-areas">
            <MapPin className="h-4 w-4 text-slate-500 mt-0.5 flex-shrink-0" />
            <div>
              {availability.outward_postcode && (
                <p className="text-xs text-slate-500">
                  Based near <span className="font-semibold text-slate-700" data-testid="outward-postcode">{availability.outward_postcode}</span>
                </p>
              )}
              {availability.work_areas?.length > 0 && (
                <p className="text-xs text-slate-500 mt-0.5">
                  Willing to work in:{' '}
                  <span className="font-medium text-slate-700">
                    {availability.work_areas.slice(0, isUnlocked ? 8 : 3).join(', ')}
                  </span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
